import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import { Grid } from '@mui/material';
import translations from './userprofile.i18n';
import * as css from './userprofile.module.scss';
import Icon, { IconType } from '../components/icon';
import classnames from 'classnames';
import { Textarea } from '../components/input';
import { TextInput } from '../components/input/textInput';
import validateEmail from '../util/validateEmail';
import validatePhoneNo, {
  phoneNoRegexPatternNotRequired,
} from '../util/validatePhoneNo';
import { ListItem, OrderedList } from '../components/list/list';

export interface Bruker {
  id: string;
  fornavn: string;
  etternavn: string;
  epost: string;
  telefonnummer: string;
}

export interface ProfilData {
  bruker: Bruker;
  praksisstedNavn: string;
  omMeg?: string;
  bildeUrl?: string;
  kanRedigere?: boolean;
}

interface UserProfileProps {
  brukerprofil: ProfilData;
  saveHandler(
    fornavn: string,
    etternavn: string,
    epost: string,
    telefonnummer: string,
    omMeg: string,
  ): void;
  editableFields: string[];
}

const getInitials = function (fornavn: string, etternavn: string) {
  let initials = '';
  for (const name of `${fornavn} ${etternavn}`.split(/\s+/)) {
    initials += name[0];
  }
  return initials;
};

const PraksisstedUserprofile = React.memo<UserProfileProps>(
  ({ brukerprofil, saveHandler, editableFields }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const { bruker, praksisstedNavn, bildeUrl, kanRedigere } = brukerprofil;
    const initials = getInitials(bruker.fornavn, bruker.etternavn);

    const [omMeg, setOmMeg] = useState(brukerprofil.omMeg || '');
    const [epost, setEpost] = useState(bruker.epost || '');
    const [tlfNr, setTlfNr] = useState(bruker.telefonnummer || '');
    const [fornavn, setFornavn] = useState(bruker.fornavn || '');
    const [etternavn, setEtternavn] = useState(bruker.etternavn || '');
    const [validInput, setValidInput] = useState(true);

    useEffect(
      () =>
        setValidInput(
          validateEmail(epost) && (!tlfNr || validatePhoneNo(tlfNr)),
        ),
      [epost, tlfNr],
    );

    const saveProfile = (event: FormEvent) => {
      event.preventDefault();
      saveHandler && saveHandler(fornavn, etternavn, epost, tlfNr, omMeg);
      setIsEditMode(false);
    };

    const cancelEdit = () => {
      setFornavn(bruker.fornavn || '');
      setEtternavn(bruker.etternavn || '');
      setTlfNr(bruker.telefonnummer || '');
      setEpost(bruker.epost || '');
      setOmMeg(brukerprofil.omMeg || '');
      setIsEditMode(false);
    };

    const fieldIsEditable = useCallback(
      (fieldName: string) => editableFields.includes(fieldName),
      [editableFields],
    );

    const allFieldsFilledIn = useMemo(
      () =>
        !(
          (fieldIsEditable('fornavn') && fornavn === '') ||
          (fieldIsEditable('etternavn') && etternavn === '') ||
          (fieldIsEditable('epost') && epost === '')
        ),
      [fornavn, etternavn, epost, fieldIsEditable],
    );

    const formId = 'contact-info-form';

    return (
      <div>
        <Grid
          container={true}
          className={classnames(css.container, isEditMode ? css.editMode : '')}
        >
          {isEditMode && (
            <form
              id={formId}
              action={''}
              method={'post'}
              onSubmit={saveProfile}
            />
          )}
          {/* Action buttons */}
          <Grid item={true} xs={12}>
            <ActionButtons
              cancel={cancelEdit}
              kanRedigere={kanRedigere || false}
              kanLagre={allFieldsFilledIn && validInput}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              formId={formId}
            />
          </Grid>

          {/* Left side profile image */}
          <Grid item={true} xs={3}>
            <ReadBilde bildeUrl={bildeUrl} initials={initials} />
          </Grid>

          {/* Right side profile info */}
          <Grid item={true} xs={8}>
            <Grid container={true}>
              <Grid item={true} xs={12}>
                <Name
                  bruker={bruker}
                  fornavn={fornavn}
                  setFornavn={setFornavn}
                  etternavn={etternavn}
                  setEtternavn={setEtternavn}
                  isEditMode={isEditMode}
                  fieldIsEditable={fieldIsEditable}
                  formId={formId}
                />
              </Grid>
              <Grid item={true} xs={12}>
                <Grid container={true} spacing={1}>
                  <Grid item={true} xs={4}>
                    <div
                      className={
                        isEditMode
                          ? classnames(css.marginFormElements, css.iconLine)
                          : css.iconLine
                      }
                    >
                      <Icon type={IconType.EndNode} />{' '}
                      <p>{` ${praksisstedNavn}`}</p>
                    </div>
                  </Grid>
                  <Grid item={true} xs={4} className="telefonnummer">
                    <Telefonnummer
                      bruker={bruker}
                      tlfNr={tlfNr}
                      setTlfNr={setTlfNr}
                      isEditMode={isEditMode}
                      fieldIsEditable={fieldIsEditable}
                      formId={formId}
                    />
                  </Grid>
                  <Grid item={true} xs={4} className="epost">
                    <Epost
                      bruker={bruker}
                      epost={epost}
                      isEditMode={isEditMode}
                      fieldIsEditable={fieldIsEditable}
                      handleEmail={setEpost}
                      formId={formId}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item={true} xs={12}>
                <OmMeg
                  initialOmMeg={brukerprofil.omMeg}
                  omMeg={omMeg}
                  setOmMeg={setOmMeg}
                  isEditMode={isEditMode}
                  fieldIsEditable={fieldIsEditable}
                  formId={formId}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  },
);

const ActionButtons: React.FC<
  React.PropsWithChildren<{
    cancel: () => void;
    kanRedigere: boolean;
    kanLagre: boolean;
    isEditMode: boolean;
    setIsEditMode: (isEditMode: boolean) => void;
    formId: string;
  }>
> = ({ formId, cancel, kanRedigere, kanLagre, isEditMode, setIsEditMode }) => {
  const { formatMessage } = useIntl();
  const labels = {
    endre: formatMessage({ id: translations.endre.id }),
    avbryt: formatMessage({ id: translations.avbryt.id }),
    lagre: formatMessage({ id: translations.lagre.id }),
  };

  return (
    <div className={css.buttonContainer}>
      {!isEditMode && kanRedigere && (
        <Button.Secondary
          onClick={() => setIsEditMode(true)}
          className="changeButton"
        >
          {labels.endre}
        </Button.Secondary>
      )}
      {isEditMode && (
        <>
          <Button.Tertiary onClick={cancel}>{labels.avbryt}</Button.Tertiary>
          <Button.Secondary
            className="saveButton"
            disabled={!kanLagre}
            form={formId}
          >
            {labels.lagre}
          </Button.Secondary>
        </>
      )}
    </div>
  );
};

const ReadBilde: React.FC<
  React.PropsWithChildren<{
    bildeUrl: string | undefined;
    initials: string;
  }>
> = ({ bildeUrl, initials }) => {
  const { formatMessage } = useIntl();
  return (
    <>
      {bildeUrl && (
        <img
          aria-hidden="true"
          src={bildeUrl}
          className={css.profilePicture}
          alt={formatMessage({ id: translations.profileImgAltText.id })}
        />
      )}
      {!bildeUrl && (
        <div
          aria-hidden="true"
          className={classnames(
            css.profilePictureReplacement,
            'profilePictureReplacement',
          )}
        >
          <p>{initials}</p>
        </div>
      )}
    </>
  );
};

const Name: React.FC<
  React.PropsWithChildren<{
    fornavn: string;
    setFornavn: (fornavn: string) => void;
    etternavn: string;
    setEtternavn: (etternavn: string) => void;
    bruker: Bruker;
    isEditMode: boolean;
    fieldIsEditable: (fieldName: string) => boolean;
    formId: string;
  }>
> = ({
  fornavn,
  setFornavn,
  etternavn,
  setEtternavn,
  bruker,
  isEditMode,
  fieldIsEditable,
  formId,
}) => {
  const { formatMessage } = useIntl();
  const labels = {
    fornavn: formatMessage({ id: translations.fornavn.id }),
    etternavn: formatMessage({ id: translations.etternavn.id }),
  };

  return (
    <>
      {(!isEditMode || !fieldIsEditable('fornavn')) && (
        <p className={css.name}>{`${bruker.fornavn} ${bruker.etternavn}`}</p>
      )}
      {isEditMode && fieldIsEditable('fornavn') && (
        <Grid container={true} spacing={1}>
          <Grid item={true} xs={4}>
            <TextInput
              htmlId={'fornavn'}
              label={labels.fornavn}
              value={fornavn}
              onChange={setFornavn}
              form={formId}
              required
            />
          </Grid>
          <Grid item={true} xs={4}>
            <TextInput
              htmlId={'etternavn'}
              label={labels.etternavn}
              value={etternavn}
              onChange={setEtternavn}
              form={formId}
              required
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

const Telefonnummer: React.FC<
  React.PropsWithChildren<{
    tlfNr: string;
    setTlfNr: (tlf: string) => void;
    bruker: Bruker;
    isEditMode: boolean;
    fieldIsEditable: (fieldName: string) => boolean;
    formId: string;
  }>
> = ({ tlfNr, setTlfNr, bruker, isEditMode, fieldIsEditable, formId }) => {
  const { formatMessage } = useIntl();
  const labels = {
    telefon: formatMessage({ id: translations.telefon.id }),
  };

  return (
    <>
      {(!isEditMode || !fieldIsEditable('telefonnummer')) && (
        <p>
          <span className={css.label}>{labels.telefon}</span>{' '}
          {bruker.telefonnummer}
        </p>
      )}
      {isEditMode && fieldIsEditable('telefonnummer') && (
        <TextInput
          htmlId={'telefon'}
          label={labels.telefon}
          value={tlfNr}
          onChange={setTlfNr}
          type={'tel'}
          pattern={phoneNoRegexPatternNotRequired}
          form={formId}
        />
      )}
    </>
  );
};

const Epost: React.FC<
  React.PropsWithChildren<{
    epost: string;
    bruker: Bruker;
    isEditMode: boolean;
    fieldIsEditable: (fieldName: string) => boolean;
    handleEmail: (value: string) => void;
    formId: string;
  }>
> = ({ epost, bruker, isEditMode, fieldIsEditable, handleEmail, formId }) => {
  const { formatMessage } = useIntl();
  const labels = {
    epost: formatMessage({ id: translations.epost.id }),
  };

  return (
    <>
      {(!isEditMode || !fieldIsEditable('epost')) && (
        <p>
          <span className={css.label}>{labels.epost}</span> {bruker.epost}
        </p>
      )}
      {isEditMode && fieldIsEditable('epost') && (
        <TextInput
          htmlId={'epost'}
          label={labels.epost}
          value={epost}
          onChange={handleEmail}
          type={'email'}
          form={formId}
          required
        />
      )}
    </>
  );
};

const OmMeg: React.FC<
  React.PropsWithChildren<{
    omMeg: string;
    setOmMeg: (tlf: string) => void;
    initialOmMeg: string | undefined;
    isEditMode: boolean;
    fieldIsEditable: (fieldName: string) => boolean;
    formId: string;
  }>
> = ({
  omMeg,
  setOmMeg,
  initialOmMeg,
  isEditMode,
  fieldIsEditable,
  formId,
}) => {
  const { formatMessage } = useIntl();
  const labels = {
    omMeg: formatMessage({ id: translations.omMeg.id }),
  };

  return (
    <>
      <Grid container={true}>
        <Grid item={true} xs={2}>
          <label className={css.aboutMeHeader} htmlFor={'om-meg'}>
            {labels.omMeg}
          </label>
        </Grid>
        <Grid item={true} xs={10}>
          <hr />
        </Grid>
        <Grid
          item={true}
          xs={12}
          className={classnames(css.aboutMeText, 'omMeg')}
        >
          {(!isEditMode || !fieldIsEditable('omMeg')) && <p>{initialOmMeg}</p>}
          {isEditMode && fieldIsEditable('omMeg') && (
            <>
              <Textarea
                htmlId={'om-meg'}
                value={omMeg}
                onChange={(event) => setOmMeg(event.target.value)}
                rows={10}
                form={formId}
              />
              <OmMegHelpText />
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const OmMegHelpText = () => {
  const { formatMessage } = useIntl();
  const {
    omMegHelpText,
    omMegHelpText1,
    omMegHelpText2,
    omMegHelpText3,
    omMegHelpText4,
  } = {
    omMegHelpText: formatMessage({ id: translations.omMegHelpText.id }),
    omMegHelpText1: formatMessage({ id: translations.omMegHelpText1.id }),
    omMegHelpText2: formatMessage({ id: translations.omMegHelpText2.id }),
    omMegHelpText3: formatMessage({ id: translations.omMegHelpText3.id }),
    omMegHelpText4: formatMessage({ id: translations.omMegHelpText4.id }),
  };

  return (
    <div role="alert" className={css.omMegHelpText}>
      <div className={css.icon}>
        <Icon type={IconType.Alert} />
      </div>
      <div className={css.text}>
        <p>{omMegHelpText}</p>
        <OrderedList>
          <ListItem>{omMegHelpText1}</ListItem>
          <ListItem>{omMegHelpText2}</ListItem>
          <ListItem>{omMegHelpText3}</ListItem>
          <ListItem>{omMegHelpText4}</ListItem>
        </OrderedList>
      </div>
    </div>
  );
};

export default PraksisstedUserprofile;
